import { defineStore } from 'pinia'
import beApiClient from "@/src/be-api-client";
import {APIRoute, EP, APIBaseRoute} from "@/src/endpoints";
import { handleErrors, makeRequest } from "@/src/utils";

export const useChecklistStore = defineStore('checklist', {
  state: ()=> {
    return {
      // lookup data
      categories: [],
      templates: [],
      transplantCenters: [],
      itemStatuses: [],
      organGroups: [],
      template_items: [],
      itemTypes: [],
      checklistTemplatesAvailable: [],
      checklistTemplatesApplicable: [],
      matchTemplates: [],
      checklistItemTypes: []
    }
  },
  getters: {
    translateOrganGroup: (state) => (id) => {
      const found = state.organGroups.find((row) => { return row.id === id; });
      return found ? found.name : null;
    },
    translateTxcCode: (state) => (id) => {
      const found = state.transplantCenters.find((row) => { return row.id === id; });
      return found ? found.code : null;
    },
    translateCategoryName: (state) => (id) =>  {
      const found = state.categories.find((row) => { return row.id === id; });
      return found ? found.category_name : null;
    },
    getItemStatuses() {
      return this.itemStatuses;
    }
  },
  actions: {
    // Generic load data method
    loadEntity(url, key, params = {}) {
      return new Promise((resolve, reject) => {
        beApiClient.get(url, { headers: {'Accept': 'application/json'}, params: params}).then(response => {
          this[key] = response.data && response.data.hasOwnProperty('data') ? response.data.data : response.data;
          resolve();
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        });
      });
    },
    loadPaginatedEntity(url, key, params = {}) {
      return new Promise((resolve, reject) => {
        beApiClient.get(url, {
          headers: {'Accept': 'application/json'},
          params: params })
          .then(response => {
          resolve(response.data);
        }).catch(error => {
          const message = handleErrors(error);
          reject(message);
        });
      });
    },

    // load transplant centres
    loadTransplantCentres() {
      return this.loadEntity(APIRoute(EP.admin.transplant_centers.index), 'transplantCenters')
    },
    // load organ groups
    loadOrganGroups() {
      return this.loadEntity(APIRoute(EP.admin.organ_groups.index), 'organGroups')
    }, 


    // Checklist Template categories
    loadPaginatedCategories(params) {
      return this.loadPaginatedEntity(APIRoute(EP.admin.match.checklist.categories.index), 'categories',params)
    },
    loadCategories() {
      return this.loadEntity(APIRoute(EP.admin.match.checklist.categories.index_all), 'categories');
    },
    loadCategory(id) {
      return makeRequest(beApiClient.get, APIRoute(EP.admin.match.checklist.categories.show, { id: id }));
    },
    createCategory(payload) {
      return makeRequest(beApiClient.post, APIRoute(EP.admin.match.checklist.categories.create), { category: payload });
    },
    updateCategory(payload) {
      return makeRequest(beApiClient.patch, APIRoute(EP.admin.match.checklist.categories.update, { id: payload.id }), { category: payload });
    },


    // Checklist Item Types
    loadPaginatedItemTypes(params) {
      return this.loadPaginatedEntity(APIRoute(EP.admin.match.checklist.item_types.index), 'itemTypes',params)
    },
    loadItemTypes() {
      return this.loadEntity(APIRoute(EP.admin.match.checklist.item_types.index_all), 'itemTypes');
    },
    loadItemType(id) {
      return makeRequest(beApiClient.get, APIRoute(EP.admin.match.checklist.item_types.show, { id: id }));
    },
    createItemType(payload) {
      return makeRequest(beApiClient.post, APIRoute(EP.admin.match.checklist.item_types.create), { item: payload });
    },
    updateItemType(payload) {
      return makeRequest(beApiClient.patch, APIRoute(EP.admin.match.checklist.item_types.update, { id: payload.id }), { item: payload });
    },


    // Checklist Templates
    loadTemplates(params) {
      return this.loadPaginatedEntity(APIRoute(EP.admin.match.checklist.templates.index, params), 'templates',params)
    },
    loadTemplate(id) {
      return makeRequest(beApiClient.get, APIRoute(EP.admin.match.checklist.templates.show, { id: id }));
    },
    createTemplate(payload) {
      return makeRequest(beApiClient.post, APIRoute(EP.admin.match.checklist.templates.create), { checklist_template: payload });
    },
    updateTemplate(payload) {
      return makeRequest(beApiClient.patch, APIRoute(EP.admin.match.checklist.templates.update, { id: payload.id }), { checklist_template: payload });
    },
    disableTemplate(templateId) {
      return makeRequest(beApiClient.put, APIRoute(EP.admin.match.checklist.templates.disable, { template_id: templateId }));
    },
    enableTemplate(templateId) {
      return makeRequest(beApiClient.put, APIRoute(EP.admin.match.checklist.templates.enable, { template_id: templateId }));
    },
   

    // Checklist Template Items
    loadItems(id) {
      return this.loadPaginatedEntity(APIRoute(EP.admin.match.checklist.items.index, { template_id: id }), 'template_items')
    },
    createTemplateItem(payload) {
      return makeRequest(beApiClient.post, APIRoute(EP.admin.match.checklist.items.create, { template_id: payload.checklist_template_id }), { checklist_template_item: payload });
    },
    updateTemplateItem(payload) {
      return makeRequest(beApiClient.patch, APIRoute(EP.admin.match.checklist.items.update, { template_id: payload.checklist_template_id, id: payload.id }), { checklist_template_item: payload });
    },
    disableItem(itemId, checklistTemplateId) {
      return makeRequest(beApiClient.put, APIRoute(EP.admin.match.checklist.items.disable, { template_id: checklistTemplateId, item_id: itemId }));
    },
    enableItem(itemId, checklistTemplateId) {
      return makeRequest(beApiClient.put, APIRoute(EP.admin.match.checklist.items.enable, { template_id: checklistTemplateId, item_id: itemId }));
    },
    deleteItem(itemId, checklistTemplateId) {
      return makeRequest(beApiClient.put, APIRoute(EP.admin.match.checklist.items.delete_item, { template_id: checklistTemplateId, item_id: itemId }));
    },
    updateItemOrder(payload, checklistTemplateId) {
      return makeRequest(beApiClient.post, APIRoute(EP.admin.match.checklist.items.update_display_order, { template_id: checklistTemplateId }), { checklist_template_items: payload });
    },
  

    // Checklist Offer
    loadMatch(id) {
      return makeRequest(beApiClient.get, APIRoute(EP.matches.show, { id: id }), { params: { lite: true } });
    },
    loadChecklistItemStatuses(matchId) {
      return this.loadEntity(APIRoute(EP.matches.checklists.lookups.checklist_application_item_statuses, { match_id: matchId }), 'itemStatuses');
    },
    loadChecklistItemTypes(matchId) {
      return this.loadEntity(APIRoute(EP.matches.checklists.lookups.checklist_application_item_types, { match_id: matchId }), 'checklistItemTypes');
    },
    loadChecklistTemplatesAvailable(matchId, checklistApplicationId = null) {
      const params = { type: 'available' };
      if (checklistApplicationId) params.checklist_application_id = checklistApplicationId;
      return this.loadEntity(APIRoute(EP.matches.checklists.checklist_templates.index, { match_id: matchId }), 'checklistTemplatesAvailable', params)
    },
    loadChecklistTemplatesApplicable(matchId) {
      return this.loadEntity(APIRoute(EP.matches.checklists.checklist_templates.index, { match_id: matchId }), 'checklistTemplatesApplicable')
    },
    loadAvailableTemplates(matchId) {
      return this.loadEntity(APIRoute(EP.matches.checklists.templates.available_templates, { match_id: matchId }), 'availableTemplates');
    },  
    loadMatchAppliedTemplates(params) {
      return this.loadPaginatedEntity(APIRoute(EP.matches.checklists.applied_templates.index, params), 'matchTemplates', params)
    },
    startChecklistForMatch(matchId, checklistName) {
      return makeRequest(beApiClient.post, APIRoute(EP.matches.checklists.checklist_applications.create, { match_id: matchId }), { checklist_name: checklistName });
    },
    updateChecklistForMatch(matchId, payload) {
      return makeRequest(beApiClient.patch, APIRoute(EP.matches.checklists.checklist_applications.update, { match_id: matchId, id: payload.checklist_application_id }), payload);
    },
    updateTemplateItemForMatch(payload) {
      return makeRequest(beApiClient.patch, APIRoute(EP.matches.checklists.applied_templates.applied_items.update, { match_id: payload.match_id, applied_template_id: payload.applied_template_id, id: payload.applied_item_id }), { checklist_applied_item: payload });
    },
    addTemplateForMatch(payload) {
      return makeRequest(beApiClient.post, APIRoute(EP.matches.checklists.checklist_templates.apply_template, { match_id: payload.match_id, checklist_template_id: payload.checklist_template_id }), { checklist_application_id: payload.checklist_application_id });
    },
    signOffForMatch(matchId, payload) {
      return makeRequest(beApiClient.patch, APIRoute(EP.matches.checklists.sign_off_verify, { match_id: matchId, checklist_application_id: payload.checklist_application_id }), { match: payload });
    },
    undoSignOffForMatch(matchId, checklistApplicationId) {
      return makeRequest(beApiClient.patch, APIRoute(EP.matches.checklists.sign_off_unverify, { match_id: matchId, checklist_application_id: checklistApplicationId }));
    },
    signOffTemplateItemForMatch(payload) {
      return makeRequest(beApiClient.post, APIRoute(EP.matches.checklists.applied_templates.applied_items.sign_off, { match_id: payload.match_id, applied_template_id: payload.applied_template_id, item_id: payload.applied_item_id }), { checklist_applied_item: payload });
    },
    updateAndSignOffTemplateItemForMatch(payload) {
      return makeRequest(beApiClient.post, APIRoute(EP.matches.checklists.applied_templates.applied_items.update_and_sign_off, { match_id: payload.match_id, applied_template_id: payload.applied_template_id, item_id: payload.applied_item_id }), { checklist_applied_item: payload });
    },
    updateStatus(payload) {
      return makeRequest(beApiClient.post, APIRoute(EP.matches.checklists.applied_templates.applied_items.bulk_update), { checklist_applied_item: payload });
    }
  }
});
